import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {ActionMethod} from 'constants/AnalyticsConstants';
import ExternalFile from 'external-files/ExternalFile';
import {
  Entry,
  EntryDay,
  EntryProtocol,
  EntryVersion,
  FromServerEntryProtocol,
  FromServerId,
  Selector,
  ServerEntryVersion,
  ToServerId,
} from 'notebook/ELNConstants';
import * as EntryDayOps from 'notebook/entries/EntryDayOps';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import * as NoteStoreTransforms from 'notebook/notes/NoteStoreTransforms';
import ProtocolStoreTransforms, {
  ProtocolStoreTransformsProtocol,
} from 'notebook/protocols/ProtocolStoreTransforms';
import prepareStructuredTableMappingsForSave from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForSave';
import prepareStructuredTableMappingsForStore from 'notebook/structured-tables/structured-table-mappings/prepareStructuredTableMappingsForStore';
import {StructuredTableMapping} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingConstants';
import {trackUpdateStructuredTableMappingsEvent} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsAnalytics';
import {StructuredTableMappingsStateManager} from 'notebook/structured-tables/structured-table-mappings/StructuredTableMappingsState';
import getAllStructuredTablesInNote from 'notebook/structured-tables/util/getAllStructuredTablesInNote';
import {
  EntryNote,
  ExternalFile as ExternalFileAttributes,
  NoteDay,
  ServerNote,
} from 'notebook/util/NoteUtilConstants';
import pick from 'util/pick';
import uniqueId from 'util/uniqueId';

/** Describes the server entry-version fields consumed while flattening entry days into one note. */
interface FromServerEntryVersionShape {
  /** Supports both numeric version IDs and the merged entry payload's string ID. */
  id: number | string;
  parentId: number | null;
  created_at: string;
  modified_at: string;
  creator: ServerEntryVersion<FromServerId>['creator'];
  entryProtocols: Array<FromServerEntryProtocol>;
  entryDays: Array<EntryDay<FromServerId>>;
  /** Required while flattening, then deleted from the consumed server payload. */
  selectors: Array<Selector>;
  noteRuntimeSnapshot: ServerEntryVersion<FromServerId>['noteRuntimeSnapshot'];
  /** Entry protection is stored on the flattened note rather than its server entry days. */
  isProtected?: boolean;
  /** Some legacy fixtures omit mappings even though server responses normally include them. */
  structuredTableMappings?: Array<StructuredTableMapping>;
}

/** Describes a flattened entry day with the note-version client ID used to rebuild server entry days. */
interface StoreNoteDay extends NoteDay {
  /** Tracks the server note-version ID separately from the entry-day ID. */
  noteVersionCid: string;
}

/** Describes the transient flattened note passed through the shared note store transform. */
interface FlattenedEntryNoteForStore {
  /** Entry-level creator metadata supplies the creator omitted from individual flattened note versions. */
  creator: object;
  selectors: Array<Selector>;
  noteRuntimeSnapshot: ServerEntryVersion<FromServerId>['noteRuntimeSnapshot'];
  isProtected?: boolean;
  noteParts: ServerNote<FromServerId>['noteParts'];
  /** Entry days survive the generic note transform even though its server-note type omits them. */
  days: Array<StoreNoteDay>;
}

/** Describes protocol fields that this utility reads or mutates across server and client phases. */
interface MutableEntryProtocol {
  externalFile?: ExternalFileAttributes | null;
  name?: string;
  /** Holds a server ID before store preparation and is cleared afterward. */
  id?: number | string | null;
  /** Identifies the entry-protocol wrapper in the client store. */
  cid?: string;
  /** Identifies the nested protocol version independently from its entry-protocol wrapper. */
  pvCid?: string;
  /** Retains the source protocol ID while the entry-specific wrapper gets its own ID. */
  originalProtocolId?: string | null;
  /** Marks a protocol copy as attached to an entry. */
  inEntry?: boolean;
}

/** Describes either external-file metadata or protocol data accepted when adding an entry protocol. */
interface ProtocolToAdd {
  externalFile?: ExternalFileAttributes | null;
  /** External files derive their display name only after this utility receives them. */
  name?: string;
  /** Becomes originalProtocolId when wrapping a source protocol for an entry. */
  id?: number | string | null;
}

/** Describes the client entry-version fields shared by entries, templates, documents, and workflows. */
interface ClientEntryVersionFields {
  note: EntryNote;
  entryProtocols: Array<EntryProtocol>;
  /** Legacy workflow and test shapes may omit the separately managed mappings array. */
  structuredTableMappings?: Array<StructuredTableMapping>;
}

/** Describes the mutable client entry-version assembled during server-to-store preparation. */
interface ClientEntryVersionForStore {
  /** Replaces the server ID during store preparation. */
  cid: string;
  /** Exists only until the server ID is registered in the client ID map. */
  id?: number | string;
  parentId: number | null;
  created_at: string;
  modified_at: string;
  creator: ServerEntryVersion<FromServerId>['creator'];
  entryProtocols: Array<MutableEntryProtocol>;
  /** Changes from the flattened server-note shape to EntryNote after the shared note transform runs. */
  note: FlattenedEntryNoteForStore | EntryNote;
  structuredTableMappings?: Array<StructuredTableMapping>;
}

/** Describes the nested protocol-version payload consumed by EntryProtocolPatcher when saving. */
interface ToServerNestedEntryProtocol {
  /** Identifies the entry-protocol wrapper independently from its nested protocol version. */
  cid: string;
  name: string;
  /** Restores the server ID for an existing entry-protocol wrapper. */
  id?: number | string;
  externalFile?: ExternalFileAttributes;
  /** Retains the source protocol ID for revert and version tracking. */
  originalProtocolId?: string | null;
  /** Carries the protocol version under the nesting expected by EntryProtocolPatcher. */
  protocolVersion?: ReturnType<typeof ProtocolStoreTransforms.preparePVForSave>;
}

/** Describes the reduced note nested under each reconstructed server entry day. */
interface ToServerEntryDayNote {
  /** Restores the note-version server ID associated with this entry day. */
  id?: number | string;
  /** Preserves the note-version client ID in the outgoing payload. */
  cid: string;
  noteParts: ReturnType<typeof NoteStoreTransforms.prepareForSave>['noteParts'];
}

/** Describes an entry day reconstructed from the client's flattened entry note. */
interface ToServerEntryDay {
  date: string;
  title?: string;
  /** Restores the entry-day server ID associated with this client day. */
  id?: number | string;
  /** Preserves the entry-day client ID in the outgoing payload. */
  cid: string;
  provenanceUuid: string;
  sectionColor?: NoteDay['sectionColor'];
  note: ToServerEntryDayNote;
}

/** Describes the client fields selected as the starting point for save preparation. */
interface ClientEntryVersionForSave {
  /** Identifies the client entry version whose server ID will be restored. */
  cid: string;
  /** Restores the server entry-version ID from the client ID map. */
  id?: number | string;
  entryProtocols: Array<MutableEntryProtocol>;
  note: EntryNoteForSave;
}

/** Describes the flattened client note after entry-day note-version IDs have been registered. */
interface EntryNoteForSave extends EntryNote {
  days: Array<StoreNoteDay>;
}

/** Describes the transient save payload before its prepared note is removed. */
interface EntryVersionForSave {
  /** Preserves the client entry-version ID in the outgoing payload. */
  cid: string;
  /** Restores the server entry-version ID from the client ID map. */
  id?: number | string;
  entryProtocols: Array<ToServerNestedEntryProtocol>;
  /** Remains optional solely so the transform can delete the flattened note before returning. */
  note?: EntryNoteForSave | ReturnType<typeof NoteStoreTransforms.prepareForSave>;
  entryDays?: Array<ToServerEntryDay>;
  selectors?: Array<Selector>;
  noteRuntimeSnapshot?: ReturnType<typeof NoteStoreTransforms.prepareForSave>['noteRuntimeSnapshot'];
  isProtected?: boolean;
  structuredTableMappings?: Array<StructuredTableMapping>;
}

/** Defines the public surface of the legacy object-style EntryVersionUtil module. */
interface EntryVersionUtilShape {
  pickRevertableFields: (entryVersion: Entry | EntryVersion) => ClientEntryVersionFields;
  // These identifiers remain optional because existing workflow callers and fixtures omit them. Make them
  // required after those callers thread real document/file ids through the transforms.
  sanitizeEntryVersion: <T extends ClientEntryVersionFields>(
    entryVersion: T,
    oldEntryVersion: T | null | undefined,
    documentId?: string
  ) => T;
  prepareExternalProtocolForStore: (
    externalProtocol: MutableEntryProtocol,
    cidIdMap: CidIdMap
  ) => MutableEntryProtocol;
  sanitizeEP: (entryProtocol: MutableEntryProtocol) => EntryProtocol;
  prepareForStore: (
    entryVersion: FromServerEntryVersionShape,
    cidIdMap: CidIdMap,
    fileId: string
  ) => EntryVersion;
  prepareForSave: (
    entryVersion: Entry | EntryVersion,
    cidIdMap: CidIdMap,
    fileId?: string | null
  ) => EntryVersionForSave & ServerEntryVersion<ToServerId>;
  prepareEPForStore: (entryProtocol: MutableEntryProtocol, cidIdMap: CidIdMap) => EntryProtocol;
  prepareEPForSave: (entryProtocol: MutableEntryProtocol, cidIdMap: CidIdMap) => ToServerNestedEntryProtocol;
  withEntryProtocolUpdated: <T extends ClientEntryVersionFields>(
    entryVersion: T,
    protocolCid: string,
    op: Spec<EntryProtocol>
  ) => T;
  withProtocolAdded: <T extends ClientEntryVersionFields>(
    entryVersion: T,
    protocol: ProtocolToAdd,
    cidIdMap: CidIdMap
  ) => T;
  withEntryProtocolRemoved: <T extends ClientEntryVersionFields>(entryVersion: T, epCid: string) => T;
  withEntryProtocolReverted: <T>(entryVersion: T, revertedEntryProtocol: EntryProtocol) => T;
  withEntryProtocolsReordered: <T extends ClientEntryVersionFields>(
    entryVersion: T,
    cids: Array<string>
  ) => T;
}

const EntryVersionUtil = {} as EntryVersionUtilShape;

EntryVersionUtil.pickRevertableFields = (entryVersion: Entry | EntryVersion): ClientEntryVersionFields =>
  pick(entryVersion, 'note', 'entryProtocols', 'structuredTableMappings');

EntryVersionUtil.sanitizeEntryVersion = function <T extends ClientEntryVersionFields>(
  entryVersion: T,
  oldEntryVersion: T | null | undefined,
  documentId?: string
): T {
  const sanitizedEntryVersionMergeOp = {
    note: NoteStoreTransforms.sanitizeNote(entryVersion.note, oldEntryVersion?.note),
    entryProtocols: Array.from(entryVersion.entryProtocols).map((p) => EntryVersionUtil.sanitizeEP(p)),
    structuredTableMappings: sanitizeStructuredTableMappings(entryVersion),
  };

  // reference equality is acceptable here because we keep the same table mappings array if it's unchanged
  if (sanitizedEntryVersionMergeOp.structuredTableMappings !== entryVersion.structuredTableMappings) {
    trackUpdateStructuredTableMappingsEvent(
      ActionMethod.SANITIZE_NOTE,
      documentId!,
      undefined,
      sanitizedEntryVersionMergeOp.structuredTableMappings ?? [],
      getAllStructuredTablesInNote(sanitizedEntryVersionMergeOp.note),
      null
    );
  }

  return reactUpdate<ClientEntryVersionFields>(entryVersion, {
    $merge: sanitizedEntryVersionMergeOp,
  }) as T;
};

// Returns a copy of entryVersion.structuredTableMappings without invalid mappings, such as those that
// reference a deleted table or column.
function sanitizeStructuredTableMappings(
  entryVersion: ClientEntryVersionFields
): Array<StructuredTableMapping> | undefined {
  const mappings = entryVersion.structuredTableMappings;
  if (!mappings?.length) {
    return mappings;
  }

  return StructuredTableMappingsStateManager.removeInvalidColumnMappings(
    StructuredTableMappingsStateManager.setStructuredTableMappings(mappings),
    getAllStructuredTablesInNote(entryVersion.note)
  ).structuredTableMappings;
}

EntryVersionUtil.prepareExternalProtocolForStore = function (
  externalProtocol: MutableEntryProtocol,
  cidIdMap: CidIdMap
): MutableEntryProtocol {
  externalProtocol.cid = uniqueId('ef');
  cidIdMap[externalProtocol.cid] = externalProtocol.id!;
  delete externalProtocol.id;
  return externalProtocol;
};

EntryVersionUtil.sanitizeEP = function (entryProtocol: MutableEntryProtocol): EntryProtocol {
  if (entryProtocol.externalFile != null) {
    return entryProtocol as EntryProtocol;
  }
  const sanitizedEntryProtocol = ProtocolStoreTransforms.sanitizeProtocol(
    entryProtocol as ProtocolStoreTransformsProtocol
  ) as MutableEntryProtocol;
  return sanitizedEntryProtocol as EntryProtocol;
};

/**
 * Prepares an EntryVersion that is from the server to be loaded into the client side store.  This includes populating the `cidIdMap` and flattening
 * out the Entry Days and NoteParts.
 *
 * WARNING: this function will mutate the `entryVersion` by deleting the `selectors` attribute, which will cause test flakes unless you have
 * cloned the `entryVersion`.  It will also mutate the `cidIdMap`.
 *
 */
EntryVersionUtil.prepareForStore = function (
  entryVersion: FromServerEntryVersionShape,
  cidIdMap: CidIdMap,
  fileId: string
): EntryVersion {
  // Normalize into entryDays and noteParts
  const days: Array<StoreNoteDay> = [];
  const noteParts: ServerNote<FromServerId>['noteParts'] = [];
  for (const entryDay of Array.from(entryVersion.entryDays)) {
    const day = EntryDayOps.makeDay(
      entryDay.date,
      entryDay.title,
      noteParts.length,
      entryDay.provenanceUuid,
      entryDay.sectionColor
    );
    cidIdMap[day.cid] = entryDay.id;
    cidIdMap[day.noteVersionCid] = entryDay.note.id;
    noteParts.push(...entryDay.note.noteParts);
    days.push(day);
  }
  const base = pick(
    entryVersion,
    'id',
    'parentId',
    'created_at',
    'modified_at',
    'creator',
    'entryProtocols'
  ) as ClientEntryVersionForStore;

  // Construct flattened note model from entryDays/noteParts with selectors pulled from entryVersion
  const note: FlattenedEntryNoteForStore = {
    creator: entryVersion.creator,
    selectors: entryVersion.selectors,
    noteRuntimeSnapshot: entryVersion.noteRuntimeSnapshot,
    isProtected: entryVersion.isProtected,
    noteParts,
    days,
  };
  // TODO (BNCH-120800): investigate why selectors object property are being deleted
  delete (entryVersion as {selectors?: Array<Selector>}).selectors;
  base.note = note;

  // Register cid/id in cidIdMap
  base.cid = uniqueId('ev');
  cidIdMap[base.cid] = base.id!;
  delete base.id;

  // move table mappings into a separate, more manageable state
  const structuredTableMappings = prepareStructuredTableMappingsForStore(
    fileId,
    cidIdMap,
    entryVersion.structuredTableMappings ?? []
  );
  base.structuredTableMappings = structuredTableMappings;

  return reactUpdate(base, {
    $merge: {
      // Entry notes intentionally add days and omit note-version metadata; the shared transform only
      // consumes their common runtime fields and preserves days.
      note: NoteStoreTransforms.prepareForStore(base.note, cidIdMap, fileId) as EntryNote,
      entryProtocols: Array.from(base.entryProtocols).map((p) =>
        EntryVersionUtil.prepareEPForStore(p, cidIdMap)
      ),
    },
  }) as EntryVersion;
};

EntryVersionUtil.prepareForSave = function (
  entryVersion: Entry | EntryVersion,
  cidIdMap: CidIdMap,
  fileId?: string | null
): EntryVersionForSave & ServerEntryVersion<ToServerId> {
  const base = pick(entryVersion, 'cid', 'entryProtocols', 'note') as ClientEntryVersionForSave;
  base.id = cidIdMap[base.cid]; // Restore id from cid

  let preparedEV = reactUpdate<EntryVersionForSave>(base as EntryVersionForSave, {
    $merge: {
      note: NoteStoreTransforms.prepareForSave(base.note, cidIdMap, fileId!),
      entryProtocols: Array.from(base.entryProtocols).map((p) =>
        EntryVersionUtil.prepareEPForSave(p, cidIdMap)
      ),
    },
  });

  // De-normalize entryDays and noteParts
  let entryDays: Array<ToServerEntryDay> = [];
  const {noteParts} = preparedEV.note as ReturnType<typeof NoteStoreTransforms.prepareForSave>;
  entryDays = base.note.days.map((day, i) => {
    const nextDay = base.note.days[i + 1];
    const note = {
      id: cidIdMap[day.noteVersionCid],
      cid: day.noteVersionCid,
      noteParts: nextDay != null ? noteParts.slice(day.index, nextDay.index) : noteParts.slice(day.index),
    };
    return {
      date: day.date,
      title: day.title,
      id: cidIdMap[day.cid],
      cid: day.cid,
      provenanceUuid: day.provenanceUuid,
      sectionColor: day.sectionColor,
      note,
    };
  });

  // Replace flattened note model with entryDays and selectors
  preparedEV.entryDays = entryDays;
  preparedEV.selectors = (preparedEV.note as ReturnType<typeof NoteStoreTransforms.prepareForSave>).selectors;
  preparedEV.noteRuntimeSnapshot = (
    preparedEV.note as ReturnType<typeof NoteStoreTransforms.prepareForSave>
  ).noteRuntimeSnapshot;
  preparedEV.isProtected = base.note.isProtected ?? false;
  delete preparedEV.note;

  // bring in the table mappings from its separate state
  const structuredTableMappings = prepareStructuredTableMappingsForSave(fileId!, cidIdMap);
  preparedEV = {
    ...preparedEV,
    structuredTableMappings,
  };
  // The existing shared save type models flattened entry protocols, while the backend patcher consumes
  // the nested protocolVersion payload produced here.
  return preparedEV as EntryVersionForSave & ServerEntryVersion<ToServerId>;
};

EntryVersionUtil.prepareEPForStore = function (
  entryProtocol: MutableEntryProtocol,
  cidIdMap: CidIdMap
): EntryProtocol {
  const {id, externalFile, originalProtocolId} = entryProtocol;

  // TODO(T1044): protocol should be keyed under protocolVersion
  // EntryProtocol dict and Protocol dict are combined, so the protocol actually
  // has two ids that correspond to it. We add a second cid so that we can properly
  // keep track of the protocol version and the entry protocol version.

  // Parse protocol and rename new fields
  if (entryProtocol.externalFile) {
    entryProtocol = EntryVersionUtil.prepareExternalProtocolForStore(entryProtocol, cidIdMap);
  } else {
    entryProtocol = ProtocolStoreTransforms.prepareForStore(
      entryProtocol as ProtocolStoreTransformsProtocol,
      cidIdMap
    ) as MutableEntryProtocol;
  }

  entryProtocol.pvCid = entryProtocol.cid;
  entryProtocol.cid = uniqueId('ep');
  cidIdMap[entryProtocol.cid] = id!;
  delete entryProtocol.id;

  // Ensure entryProtocol fields are set.
  entryProtocol.externalFile = externalFile;
  entryProtocol.originalProtocolId = originalProtocolId;

  return entryProtocol as EntryProtocol;
};

EntryVersionUtil.prepareEPForSave = function (
  entryProtocol: MutableEntryProtocol,
  cidIdMap: CidIdMap
): ToServerNestedEntryProtocol {
  const {cid, name} = entryProtocol;
  const d: ToServerNestedEntryProtocol = {
    cid: cid!,
    name: name!,
    id: cidIdMap[cid!],
  };

  // Handle external file vs. protocol
  const {externalFile, originalProtocolId, pvCid} = entryProtocol;
  if (externalFile != null) {
    d.externalFile = externalFile;
    return d;
  }

  // Otherwise, rename fields for protocol preparation.
  const protocolVersion = reactUpdate(entryProtocol, {$merge: {cid: pvCid}});
  const preparedProtocolVersion = ProtocolStoreTransforms.preparePVForSave(
    protocolVersion as ProtocolStoreTransformsProtocol,
    cidIdMap
  );

  // and add protocol fields
  d.originalProtocolId = originalProtocolId;
  d.protocolVersion = preparedProtocolVersion;
  return d;
};

EntryVersionUtil.withEntryProtocolUpdated = <T extends ClientEntryVersionFields>(
  entryVersion: T,
  protocolCid: string,
  op: Spec<EntryProtocol>
): T =>
  withEntryProtocolsSet(
    entryVersion,
    entryVersion.entryProtocols.map((entryProtocol) => {
      if (entryProtocol.cid === protocolCid) {
        entryProtocol = reactUpdate(entryProtocol, op);
      }
      return entryProtocol;
    })
  );

EntryVersionUtil.withProtocolAdded = function <T extends ClientEntryVersionFields>(
  entryVersion: T,
  protocol: ProtocolToAdd,
  cidIdMap: CidIdMap
): T {
  let entryProtocol: MutableEntryProtocol;
  if (protocol.externalFile) {
    const {externalFile} = protocol;
    entryProtocol = {
      externalFile,
      name: ExternalFile.toName(externalFile),
      originalProtocolId: null,
      inEntry: true,
      id: null,
    };
  } else {
    // eslint-disable-next-line no-restricted-syntax -- preserve original _.extend merge behavior
    entryProtocol = _.extend({}, protocol, {
      originalProtocolId: protocol.id,
      inEntry: true,
      id: null,
    }) as MutableEntryProtocol;
  }
  const preparedEntryProtocol = EntryVersionUtil.prepareEPForStore(entryProtocol, cidIdMap);
  return withEntryProtocolsSet(entryVersion, [...entryVersion.entryProtocols, preparedEntryProtocol]);
};

EntryVersionUtil.withEntryProtocolRemoved = <T extends ClientEntryVersionFields>(
  entryVersion: T,
  epCid: string
): T =>
  withEntryProtocolsSet(
    entryVersion,
    entryVersion.entryProtocols.filter((entryProtocol) => entryProtocol.cid !== epCid)
  );

EntryVersionUtil.withEntryProtocolReverted = <T>(entryVersion: T, revertedEntryProtocol: EntryProtocol): T =>
  reactUpdate<ClientEntryVersionFields>(entryVersion as T & ClientEntryVersionFields, {
    entryProtocols: {
      $set: (entryVersion as T & ClientEntryVersionFields).entryProtocols.map((entryProtocol) => {
        if (entryProtocol.cid === revertedEntryProtocol.cid) {
          return revertedEntryProtocol;
        } else {
          return entryProtocol;
        }
      }),
    },
  }) as T;

EntryVersionUtil.withEntryProtocolsReordered = <T extends ClientEntryVersionFields>(
  entryVersion: T,
  cids: Array<string>
): T =>
  withEntryProtocolsSet(
    entryVersion,
    cids.map((cid) => _.findWhere(entryVersion.entryProtocols, {cid})!)
  );

function withEntryProtocolsSet<T extends ClientEntryVersionFields>(
  entryVersion: T,
  entryProtocols: Array<EntryProtocol>
): T {
  // reactUpdate cannot validate a spec against an unresolved generic. Only entryProtocols changes, so the
  // caller's concrete Entry/EntryVersion subtype is preserved.
  return reactUpdate<ClientEntryVersionFields>(entryVersion, {
    entryProtocols: {$set: entryProtocols},
  }) as T;
}

export default EntryVersionUtil;
